import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import {
  ensureExists,
  exists,
  findByObj,
  isAssigned,
  type Unassigned,
} from '~/utils'

import {
  type CustomerAttributeGroupSettingsInputCustomerAttributeGroup,
  type CustomerAttributeGroupSettingsInputCustomerAttributeGroupDefault,
} from '../utils'

import { useMainQueryCustomerAttributeOrderTable } from './useMainQuery'

type UpdateCustomerAttributeGroupsMutationVariables = {
  customerAttributeGroups: CustomerAttributeGroupSettingsInputCustomerAttributeGroup[]
  defaultCustomerAttributeGroups: CustomerAttributeGroupSettingsInputCustomerAttributeGroupDefault[]
  surveyGroupId: string
}

const createGroupBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_CreateCustomerAttributeGroupBulk(
    $surveyGroupId: String!
    $input: [SurveyGroupCustomerAttributeGroupBulkCreateItemInput!]!
  ) {
    createSurveyGroupCustomerAttributeGroupBulk(
      surveyGroupId: $surveyGroupId
      input: $input
    ) {
      result
    }
  }
`)

const updateGroupBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_UpdateCustomerAttributeGroupBulk(
    $input: [SurveyGroupCustomerAttributeGroupBulkUpdateItemInput!]!
  ) {
    updateSurveyGroupCustomerAttributeGroupBulk(input: $input) {
      result
    }
  }
`)

const deleteGroupBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_DeleteCustomerAttributeGroupBulk(
    $surveyGroupCustomerAttributeGroupIds: [ID!]!
  ) {
    deleteSurveyGroupCustomerAttributeGroupBulk(
      surveyGroupCustomerAttributeGroupIds: $surveyGroupCustomerAttributeGroupIds
    ) {
      result
    }
  }
`)

const createBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_CreateCustomerAttributeBulk(
    $input: [SurveyGroupCustomerAttributeBulkCreateItemInput!]!
  ) {
    createSurveyGroupCustomerAttributeBulk(input: $input) {
      result
    }
  }
`)

const updateBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_UpdateCustomerAttributeBulk(
    $input: [SurveyGroupCustomerAttributeBulkUpdateItemInput!]!
  ) {
    updateSurveyGroupCustomerAttributeBulk(input: $input) {
      result
    }
  }
`)

const deleteBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_DeleteCustomerAttributeBulk(
    $surveyGroupCustomerAttributeIds: [ID!]!
  ) {
    deleteSurveyGroupCustomerAttributeBulk(
      surveyGroupCustomerAttributeIds: $surveyGroupCustomerAttributeIds
    ) {
      result
    }
  }
`)

export const useUpdateCustomerAttributeGroupsMutation = (
  options: Omit<
    UseMutationOptions<
      void,
      unknown,
      UpdateCustomerAttributeGroupsMutationVariables,
      unknown
    >,
    'mutationFn'
  > = {},
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const customerAttributeOrderTable = useMainQueryCustomerAttributeOrderTable()
  const getCustomerAttributeId = (
    customerAttributeGroup: CustomerAttributeGroupSettingsInputCustomerAttributeGroup,
    surveyId: string,
    order: Unassigned | number | null,
  ) =>
    findByObj(customerAttributeOrderTable, {
      surveyId,
      customerAttributeGroupId: customerAttributeGroup
        .attachedCustomerAttributeGroupMap[surveyId] as string,
      order: order as number,
    })?.customerAttributeId ?? null

  return useMutation({
    mutationFn: async ({
      customerAttributeGroups,
      defaultCustomerAttributeGroups,
      surveyGroupId,
    }) => {
      // UIでは顧客属性グループ->顧客属性の階層になっている。削除の順番を考えて顧客属性の処理から先に行う
      // 顧客属性を削除
      const customerAttributeIdsToDelete = R.differenceWith(
        defaultCustomerAttributeGroups
          .filter((defaultCustomerAttributeGroup) =>
            customerAttributeGroups
              .map(R.prop('id'))
              .includes(defaultCustomerAttributeGroup.id),
          )
          .flatMap(
            (defaultCustomerAttributeGroup) =>
              defaultCustomerAttributeGroup.customerAttributes,
          ),
        customerAttributeGroups.flatMap(
          (customerAttributeGroup) => customerAttributeGroup.customerAttributes,
        ),
        (defaultCustomerAttribute, customerAttribute) =>
          defaultCustomerAttribute.id === customerAttribute.id,
      ).map(R.prop('id'))
      if (0 < customerAttributeIdsToDelete.length) {
        await graphQLRequest({
          document: deleteBulkDocument,
          variables: {
            surveyGroupCustomerAttributeIds: customerAttributeIdsToDelete,
          },
        })
      }

      // 顧客属性を作成
      const groupsForCustomerAttributesToCreate: CustomerAttributeGroupSettingsInputCustomerAttributeGroup[] =
        customerAttributeGroups
          .filter(
            (customerAttributeGroup) =>
              exists(customerAttributeGroup.id) &&
              customerAttributeGroup.customerAttributes.some(
                (customerAttribute) => !exists(customerAttribute.id),
              ),
          )
          .map((customerAttributeGroup) => ({
            ...customerAttributeGroup,
            customerAttributes:
              customerAttributeGroup.customerAttributes.filter(
                (customerAttribute) => !exists(customerAttribute.id),
              ),
          }))
      if (0 < groupsForCustomerAttributesToCreate.length) {
        await graphQLRequest({
          document: createBulkDocument,
          variables: {
            input: groupsForCustomerAttributesToCreate.map(
              (customerAttributeGroup) => ({
                surveyGroupCustomerAttributeGroupId: ensureExists(
                  customerAttributeGroup.id,
                  t('assertion_failed'),
                ),
                surveyCustomerAttributes:
                  customerAttributeGroup.customerAttributes.map(
                    (customerAttribute) => ({
                      name: customerAttribute.name,
                      customerAttributes: R.pipe(
                        customerAttribute.attachedCustomerAttributeMap,
                        R.pickBy(isAssigned),
                        R.entries(),
                        R.map(([surveyId, order]) => ({
                          surveyId,
                          customerAttributeId: getCustomerAttributeId(
                            customerAttributeGroup,
                            surveyId,
                            order ?? null,
                          ),
                        })),
                      ),
                    }),
                  ),
              }),
            ),
          },
        })
      }

      // 顧客属性を更新
      const customerAttributesToUpdate = R.differenceWith(
        customerAttributeGroups
          .filter((customerAttributeGroup) => exists(customerAttributeGroup.id))
          .flatMap(
            (customerAttributeGroup) =>
              customerAttributeGroup.customerAttributes,
          )
          .filter((customerAttribute) => exists(customerAttribute.id)),
        defaultCustomerAttributeGroups.flatMap(
          (customerAttributeGroup) => customerAttributeGroup.customerAttributes,
        ),
        (customerAttribute, defaultCustomerAttribute) =>
          customerAttribute.id === defaultCustomerAttribute.id &&
          customerAttribute.name === defaultCustomerAttribute.name &&
          R.isDeepEqual(
            customerAttribute.attachedCustomerAttributeMap,
            defaultCustomerAttribute.attachedCustomerAttributeMap,
          ),
      )
      if (0 < customerAttributesToUpdate.length) {
        await graphQLRequest({
          document: updateBulkDocument,
          variables: {
            input: customerAttributesToUpdate.map((customerAttribute) => ({
              surveyGroupCustomerAttributeId: ensureExists(
                customerAttribute.id,
                t('assertion_failed'),
              ),
              name: customerAttribute.name,
              surveyCustomerAttributes: R.pipe(
                customerAttribute.attachedCustomerAttributeMap,
                R.pickBy(isAssigned),
                R.entries(),
                R.map(([surveyId, order]) => ({
                  surveyId,
                  customerAttributeId: getCustomerAttributeId(
                    ensureExists(
                      customerAttributeGroups.find((group) =>
                        group.customerAttributes.includes(customerAttribute),
                      ),
                      t('assertion_failed'),
                    ),
                    surveyId,
                    order ?? null,
                  ),
                })),
              ),
            })),
          },
        })
      }

      // 顧客属性グループを削除
      const surveyGroupCustomerAttributeGroupIdsToDelete = R.differenceWith(
        defaultCustomerAttributeGroups,
        customerAttributeGroups,
        (defaultCustomerAttributeGroup, customerAttributeGroup) =>
          defaultCustomerAttributeGroup.id === customerAttributeGroup.id,
      ).map(R.prop('id'))
      if (0 < surveyGroupCustomerAttributeGroupIdsToDelete.length) {
        await graphQLRequest({
          document: deleteGroupBulkDocument,
          variables: {
            surveyGroupCustomerAttributeGroupIds:
              surveyGroupCustomerAttributeGroupIdsToDelete,
          },
        })
      }

      // 顧客属性グループを作成
      const groupsToCreate = customerAttributeGroups.filter(
        (customerAttributeGroup) => !exists(customerAttributeGroup.id),
      )
      if (0 < groupsToCreate.length) {
        await graphQLRequest({
          document: createGroupBulkDocument,
          variables: {
            surveyGroupId,
            input: groupsToCreate.map((customerAttributeGroup) => ({
              name: customerAttributeGroup.name,
              showCustomizedKPI: customerAttributeGroup.showCustomizedKPI,
              surveyCustomerAttributeGroups: R.pipe(
                customerAttributeGroup.attachedCustomerAttributeGroupMap,
                R.pickBy(isAssigned),
                R.entries(),
                R.map(([surveyId, customerAttributeGroupId]) => ({
                  surveyId,
                  customerAttributeGroupId: customerAttributeGroupId as
                    string | null,
                })),
              ),
              surveyCustomerAttributes:
                customerAttributeGroup.customerAttributes.map(
                  (customerAttribute) => ({
                    name: customerAttribute.name,
                    customerAttributes: R.pipe(
                      customerAttribute.attachedCustomerAttributeMap,
                      R.pickBy(isAssigned),
                      R.entries(),
                      R.map(([surveyId, order]) => ({
                        surveyId,
                        customerAttributeId: getCustomerAttributeId(
                          customerAttributeGroup,
                          surveyId,
                          order ?? null,
                        ),
                      })),
                    ),
                  }),
                ),
            })),
          },
        })
      }

      // 顧客属性グループを更新
      const groupsToUpdate = R.differenceWith(
        customerAttributeGroups.filter((customerAttributeGroup) =>
          exists(customerAttributeGroup.id),
        ),
        defaultCustomerAttributeGroups,
        (customerAttributeGroup, defaultCustomerAttributeGroup) =>
          customerAttributeGroup.id === defaultCustomerAttributeGroup.id &&
          customerAttributeGroup.name === defaultCustomerAttributeGroup.name &&
          customerAttributeGroup.showCustomizedKPI ===
            defaultCustomerAttributeGroup.showCustomizedKPI &&
          R.isDeepEqual(
            customerAttributeGroup.attachedCustomerAttributeGroupMap,
            defaultCustomerAttributeGroup.attachedCustomerAttributeGroupMap,
          ),
      )
      if (0 < groupsToUpdate.length) {
        await graphQLRequest({
          document: updateGroupBulkDocument,
          variables: {
            input: groupsToUpdate.map((customerAttributeGroup) => ({
              surveyGroupCustomerAttributeGroupId: customerAttributeGroup.id!,
              name: customerAttributeGroup.name,
              showCustomizedKPI: customerAttributeGroup.showCustomizedKPI,
              surveyCustomerAttributeGroups: R.pipe(
                customerAttributeGroup.attachedCustomerAttributeGroupMap,
                R.pickBy(isAssigned),
                R.entries(),
                R.map(([surveyId, customerAttributeGroupId]) => ({
                  surveyId,
                  customerAttributeGroupId: customerAttributeGroupId as
                    string | null,
                })),
              ),
            })),
          },
        })
      }
    },
    ...options,
  })
}
